import React from 'react';
import { X, MapPin, Calendar, DollarSign, Briefcase, Building2, Star, Users, Clock } from 'lucide-react';

const JobDetailsModal = ({ isOpen, onClose, job, loading = false }) => {
    if (!isOpen || !job) return null;

    // Function to detect job source and return appropriate tag
    const getJobSourceTag = (url) => {
        if (!url || url.trim() === '') return { name: 'Apply', color: 'blue', showPlatform: false };
        
        const urlLower = url.toLowerCase().trim();
        
        if (urlLower.includes('linkedin.com')) {
            return { name: 'LinkedIn', color: 'blue', showPlatform: true };
        } else if (urlLower.includes('indeed.com')) {
            return { name: 'Indeed', color: 'green', showPlatform: true };
        } else if (urlLower.includes('glassdoor.com')) {
            return { name: 'Glassdoor', color: 'purple', showPlatform: true };
        } else if (urlLower.includes('monster.com')) {
            return { name: 'Monster', color: 'orange', showPlatform: true };
        } else if (urlLower.includes('ziprecruiter.com')) {
            return { name: 'ZipRecruiter', color: 'red', showPlatform: true };
        } else if (urlLower.includes('careerbuilder.com')) {
            return { name: 'CareerBuilder', color: 'yellow', showPlatform: true };
        } else if (urlLower.includes('dice.com')) {
            return { name: 'Dice', color: 'indigo', showPlatform: true };
        } else if (urlLower.includes('angel.co') || urlLower.includes('wellfound.com')) {
            return { name: 'Wellfound', color: 'pink', showPlatform: true };
        } else if (urlLower.includes('stackoverflow.com')) {
            return { name: 'Stack Overflow', color: 'orange', showPlatform: true };
        } else if (urlLower.includes('github.com')) {
            return { name: 'GitHub', color: 'gray', showPlatform: true };
        } else {
            return { name: 'Apply', color: 'blue', showPlatform: false };
        }
    };

    const jobSource = getJobSourceTag(job.applyLink);

    const formatSalary = (min, max) => {
        if (!min && !max) return 'Not Listed';
        if (!min) return `Up to $${max?.toLocaleString()}`;
        if (!max) return `From $${min?.toLocaleString()}`;
        return `$${min?.toLocaleString()} - $${max?.toLocaleString()}`;
    };

    const getPostedDate = (datentime) => {
        if (!datentime) return null;
        if (datentime.$date?.$numberLong) {
            return new Date(parseInt(datentime.$date.$numberLong, 10));
        }
        if (typeof datentime === "string" || typeof datentime === "number") {
            return new Date(datentime);
        }
        return null;
    };

    const postedDate = getPostedDate(job.datentime);
    const formattedDate = postedDate ? postedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }) : 'Unknown';

    const normalizeLine = (value) => String(value || '').replace(/\s+/g, ' ').trim();

    const cleanItems = (items) => {
        const seen = new Set();
        const result = [];
        const values = Array.isArray(items) ? items : [];

        for (let i = 0; i < values.length; i += 1) {
            const line = normalizeLine(values[i]);
            if (!line) continue;
            const key = line.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            result.push(line);
        }

        return result;
    };

    const sectionOrder = ['summary', 'responsibilities', 'qualifications', 'benefits', 'other'];
    const sectionLabels = {
        summary: 'Summary',
        responsibilities: 'Responsibilities',
        qualifications: 'Qualifications',
        benefits: 'Benefits',
        other: 'Other'
    };

    const buildDescriptionSections = () => {
        if (job?.descriptionSections && typeof job.descriptionSections === 'object') {
            return {
                summary: cleanItems(job.descriptionSections.summary),
                responsibilities: cleanItems(job.descriptionSections.responsibilities),
                qualifications: cleanItems(job.descriptionSections.qualifications),
                benefits: cleanItems(job.descriptionSections.benefits),
                other: cleanItems(job.descriptionSections.other)
            };
        }

        const highlights = job?.job_highlights || {};
        const summary = [];
        const description = String(job?.jobDesc || '').trim();
        if (description) {
            const firstSentence = description.split(/(?<=[.!?])\s+/)[0];
            const normalizedSummary = normalizeLine(firstSentence);
            if (normalizedSummary) summary.push(normalizedSummary.slice(0, 240));
        }

        return {
            summary: cleanItems(summary),
            responsibilities: cleanItems(highlights.Responsibilities || highlights.responsibilities),
            qualifications: cleanItems(highlights.Qualifications || highlights.qualifications),
            benefits: cleanItems(highlights.Benefits || highlights.benefits),
            other: []
        };
    };

    const descriptionSections = buildDescriptionSections();
    const nonEmptySectionOrder = sectionOrder.filter((key) => descriptionSections[key].length > 0);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-8 py-6 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                            <img
                                src={job.companyLogo || 'https://cdn-icons-png.flaticon.com/512/2721/2721294.png'}
                                alt={job.companyName}
                                className="rounded-xl border-2 border-white shadow-sm w-16 h-16"
                                onError={(e) => { e.target.src = 'https://cdn-icons-png.flaticon.com/512/2721/2721294.png'; }}
                            />
                            <div className="flex-1">
                                <h1 className="text-2xl font-bold text-gray-900 mb-2">{job.jobPosition}</h1>
                                <div className="flex items-center gap-2 text-lg text-gray-600 mb-2">
                                    <Building2 className="w-5 h-5" />
                                    <span className="font-semibold">{job.companyName}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-500">
                                    <MapPin className="w-4 h-4" />
                                    <span>{job.jobLocation}</span>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
                    <div className="p-8">
                        {/* Key Information Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                            {/* Salary */}
                            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <DollarSign className="w-5 h-5 text-green-600" />
                                    <span className="text-sm font-medium text-green-700">Salary Range</span>
                                </div>
                                <div className="text-lg font-bold text-green-800 text-left">
                                    {formatSalary(job.jobMinSalary, job.jobMaxSalary)}
                                </div>
                            </div>

                            {/* Job Type */}
                            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <Briefcase className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm font-medium text-blue-700">Job Type</span>
                                </div>
                                <div className="text-lg font-bold text-blue-800 text-left">{job.jobType}</div>
                            </div>

                            {/* Posted Date */}
                            <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                                <div className="flex items-center gap-2 mb-2">
                                    <Calendar className="w-5 h-5 text-purple-600" />
                                    <span className="text-sm font-medium text-purple-700">Posted</span>
                                </div>
                                <div className="text-lg font-bold text-purple-800 text-left">{formattedDate}</div>
                            </div>

                            {/* Job Fit Score */}
                            {job.companyId?.fitScore && (
                                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Star className="w-5 h-5 text-yellow-600" />
                                        <span className="text-sm font-medium text-yellow-700">Job Fit Score</span>
                                    </div>
                                    <div className="text-lg font-bold text-yellow-800 text-left">
                                        {job.companyId.fitScore.toString().slice(0, 5)}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Job Description */}
                        <div className="mb-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Briefcase className="w-5 h-5" />
                                Job Description
                            </h2>
                            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                                {nonEmptySectionOrder.length > 0 ? (
                                    <div className="space-y-5">
                                        {nonEmptySectionOrder.map((sectionKey) => (
                                            <div key={sectionKey} className="text-left">
                                                <h3 className="font-bold text-gray-900 mb-2 text-base">{sectionLabels[sectionKey]}</h3>
                                                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                                                    {descriptionSections[sectionKey].map((line, idx) => (
                                                        <li key={`${sectionKey}-${idx}`} className="leading-relaxed">{line}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                ) : (loading && !job.jobDesc) ? (
                                    <div className="flex items-center gap-2 text-gray-500 py-4">
                                        <span className="inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-[#3F57E0] animate-spin" />
                                        Loading description…
                                    </div>
                                ) : (
                                    <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-left">
                                        {job.jobDesc || 'Description not available.'}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Company Information */}
                        {job.companyId && (
                            <div className="mb-8">
                                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <Building2 className="w-5 h-5" />
                                    Company Information
                                </h2>
                                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                                    <div className="space-y-6">
                                        <div className="border-b border-gray-200 pb-4">
                                            <h3 className="font-bold text-gray-900 mb-3 text-left text-lg">Company Name</h3>
                                            <p className="text-gray-600 text-left">{job.companyName}</p>
                                        </div>
                                        {job.companyId.hqLocation && (
                                            <div className="border-b border-gray-200 pb-4">
                                                <h3 className="font-bold text-gray-900 mb-3 text-left text-lg">Headquarters</h3>
                                                <p className="text-gray-600 text-left">{job.companyId.hqLocation}</p>
                                            </div>
                                        )}
                                        {job.companyId.description && (
                                            <div>
                                                <h3 className="font-bold text-gray-900 mb-3 text-left text-lg">About Company</h3>
                                                <p className="text-gray-600 leading-relaxed text-left">{job.companyId.description}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Additional Details */}
                        <div className="mb-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                Additional Details
                            </h2>
                            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                                <div className="space-y-6">
                                    {job.experienceLevel && (
                                            <div className="border-b border-gray-200 pb-4">
                                                <h3 className="font-bold text-gray-900 mb-3 text-left text-lg">Experience Level</h3>
                                                <p className="text-gray-600 text-left">{job.experienceLevel}</p>
                                            </div>
                                    )}
                                    {job.jobCategory && (
                                            <div className="border-b border-gray-200 pb-4">
                                                <h3 className="font-bold text-gray-900 mb-3 text-left text-lg">Category</h3>
                                                <p className="text-gray-600 text-left">{job.jobCategory}</p>
                                            </div>
                                    )}
                                    {job.remoteWork && (
                                            <div className="border-b border-gray-200 pb-4">
                                                <h3 className="font-bold text-gray-900 mb-3 text-left text-lg">Remote Work</h3>
                                                <p className="text-gray-600 text-left">{job.remoteWork}</p>
                                            </div>
                                    )}
                                    {job.benefits && (
                                            <div>
                                            <h3 className="font-bold text-gray-900 mb-3 text-left text-lg">Benefits</h3>
                                            <p className="text-gray-600 text-left">{job.benefits}</p>
                                            </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-8 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        Close
                    </button>
                    {job.applyLink && (
                        <button
                            onClick={() => window.open(job.applyLink, '_blank')}
                            className={`px-6 py-2 text-white font-medium rounded-lg transition-colors min-w-[140px] ${
                                jobSource.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                                jobSource.color === 'green' ? 'bg-green-600 hover:bg-green-700' :
                                jobSource.color === 'purple' ? 'bg-purple-600 hover:bg-purple-700' :
                                jobSource.color === 'orange' ? 'bg-orange-600 hover:bg-orange-700' :
                                jobSource.color === 'red' ? 'bg-red-600 hover:bg-red-700' :
                                jobSource.color === 'yellow' ? 'bg-yellow-600 hover:bg-yellow-700' :
                                jobSource.color === 'indigo' ? 'bg-indigo-600 hover:bg-indigo-700' :
                                jobSource.color === 'pink' ? 'bg-pink-600 hover:bg-pink-700' :
                                jobSource.color === 'gray' ? 'bg-gray-600 hover:bg-gray-700' :
                                'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            {jobSource.showPlatform ? `Apply on ${jobSource.name}` : 'Apply Now'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JobDetailsModal;
